/**
 * Reports & Analytics — tabbed operations dashboard.
 *
 * Every tab renders its own table + a "Download CSV" button. CSV export
 * is client-side (we already have the JSON in memory, no reason to
 * ship a second ``.csv`` route per report).
 *
 * Tabs
 * ----
 *   Overview          — the original KPI dashboard (kept intact)
 *   Outstanding       — every open / overdue invoice, sorted by
 *                       days overdue
 *   Upcoming billing  — customers about to bill in the next N days
 *   Payments          — successful payments in a date range + chart
 *   Revenue           — monthly revenue trend
 *   Route performance — completion + missed rate per current route
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import KPICard from "@/components/KPICard";
import { fmtMoney, titleCase } from "@/lib/format";
import {
    DollarSign, TrendingUp, Users, Truck, Download, FileText, CalendarClock,
    CreditCard, LineChart as LineChartIcon, MapPin, RefreshCw,
} from "lucide-react";
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar,
} from "recharts";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";

const money = (n) => fmtMoney(n || 0);
const isoTodayOffset = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

/** Client-side CSV export. Escapes ``"`` and wraps every field to keep
 *  Excel happy with commas + line breaks in address / notes columns. */
function downloadCsv(filename, rows, columns) {
    if (!rows?.length) return;
    const esc = (v) => {
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return `"${s}"`;
    };
    const header = columns.map((c) => esc(c.label)).join(",");
    const body = rows.map((r) => columns.map((c) => esc(
        typeof c.value === "function" ? c.value(r) : r[c.value])).join(","));
    const csv = [header, ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export default function Reports() {
    return (
        <div data-testid="reports-page">
            <PageHeader
                title="Reports & Analytics"
                subtitle="Outstanding invoices, upcoming billing, payments, revenue trend, route performance." />
            <div className="px-8 py-6">
                <Tabs defaultValue="overview">
                    <TabsList className="mb-4 flex-wrap h-auto">
                        <TabsTrigger value="overview" data-testid="reports-tab-overview">Overview</TabsTrigger>
                        <TabsTrigger value="outstanding" data-testid="reports-tab-outstanding">Outstanding invoices</TabsTrigger>
                        <TabsTrigger value="upcoming" data-testid="reports-tab-upcoming">Upcoming billing</TabsTrigger>
                        <TabsTrigger value="payments" data-testid="reports-tab-payments">Payments</TabsTrigger>
                        <TabsTrigger value="revenue" data-testid="reports-tab-revenue">Revenue</TabsTrigger>
                        <TabsTrigger value="extras" data-testid="reports-tab-extras">Extra charges</TabsTrigger>
                        <TabsTrigger value="routes" data-testid="reports-tab-routes">Route performance</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview"><OverviewTab /></TabsContent>
                    <TabsContent value="outstanding"><OutstandingTab /></TabsContent>
                    <TabsContent value="upcoming"><UpcomingTab /></TabsContent>
                    <TabsContent value="payments"><PaymentsTab /></TabsContent>
                    <TabsContent value="revenue"><RevenueTab /></TabsContent>
                    <TabsContent value="extras"><ExtraChargesTab /></TabsContent>
                    <TabsContent value="routes"><RoutesTab /></TabsContent>
                </Tabs>

                <div className="mt-6 text-xs text-muted-foreground flex flex-wrap gap-4">
                    <span>Also see:</span>
                    <Link to="/ar-aging" className="underline">AR Aging</Link>
                    <Link to="/reports/sales-tax" className="underline">Sales Tax</Link>
                    <Link to="/profitability" className="underline">Profitability</Link>
                    <Link to="/timesheets" className="underline">Timesheets</Link>
                    <Link to="/expenses" className="underline">Expenses</Link>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------- Overview ------------------------------- */

function OverviewTab() {
    const [kpis, setKpis] = useState(null);
    useEffect(() => { api.get("/dashboard/kpis").then(({ data }) => setKpis(data)); }, []);
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPICard testid="rpt-revenue" label="Revenue MTD" value={money(kpis?.revenue_mtd)} icon={DollarSign} delta="Paid invoices" deltaTone="success" />
                <KPICard testid="rpt-customers" label="Active customers" value={kpis?.active_customers || 0} icon={Users} />
                <KPICard testid="rpt-outstanding" label="Outstanding AR" value={money(kpis?.outstanding_ar)} icon={FileText} deltaTone={kpis?.outstanding_ar > 0 ? "warning" : "success"} />
                <KPICard testid="rpt-fleet" label="Fleet active" value={`${kpis?.trucks_active || 0}/${(kpis?.trucks_active || 0) + (kpis?.trucks_maintenance || 0)}`} icon={Truck} />
            </div>
            <Card>
                <CardContent className="p-6">
                    <div className="overline mb-1">30-day window</div>
                    <h3 className="font-heading text-lg font-semibold mb-6">Daily revenue</h3>
                    <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={kpis?.revenue_trend || []}>
                                <defs>
                                    <linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} interval={4} />
                                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} />
                                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rg)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

/* --------------------------- Outstanding invoices --------------------------- */

function OutstandingTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = () => {
        setLoading(true);
        api.get("/reports/outstanding-invoices")
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);
    const csv = () => downloadCsv("outstanding-invoices.csv", data?.rows || [], [
        { label: "Invoice #", value: "invoice_number" },
        { label: "Customer", value: "customer_name" },
        { label: "Issued", value: "issued_date" },
        { label: "Due", value: "due_date" },
        { label: "Days overdue", value: "days_overdue" },
        { label: "Balance", value: (r) => r.balance.toFixed(2) },
        { label: "Total", value: (r) => r.total.toFixed(2) },
        { label: "Status", value: "status" },
    ]);
    return (
        <ReportShell
            title="Outstanding invoices"
            subtitle={data ? `${data.count} invoice(s) · ${money(data.total_outstanding)} total outstanding` : "…"}
            onRefresh={load} onCsv={csv} loading={loading}>
            <table className="w-full text-sm" data-testid="outstanding-invoices-table">
                <TableHead cols={["Invoice", "Customer", "Issued", "Due", "Days overdue", "Balance", "Status"]} />
                <tbody>
                    {(data?.rows || []).map((r) => (
                        <tr key={r.invoice_id} className="border-t border-border hover:bg-secondary/40">
                            <td className="px-3 py-2 text-xs">{r.invoice_number || <span className="text-muted-foreground">—</span>}</td>
                            <td className="px-3 py-2">
                                <Link to={`/customers?highlight=${r.customer_id}`} className="hover:underline">
                                    {r.customer_name || <span className="text-muted-foreground">(unknown)</span>}
                                </Link>
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{r.issued_date || "—"}</td>
                            <td className="px-3 py-2 text-xs">{r.due_date || "—"}</td>
                            <td className="px-3 py-2">
                                {r.days_overdue > 0
                                    ? <Badge variant="destructive" className="text-[10px]">{r.days_overdue}d late</Badge>
                                    : <span className="text-xs text-muted-foreground">on time</span>}
                            </td>
                            <td className="px-3 py-2 font-medium text-right">{money(r.balance)}</td>
                            <td className="px-3 py-2 text-xs uppercase tracking-wider">{r.status}</td>
                        </tr>
                    ))}
                    {!loading && !data?.rows?.length && (
                        <tr><td colSpan={7} className="text-center py-10 text-sm text-muted-foreground">Nothing outstanding — the ledger is clean.</td></tr>
                    )}
                </tbody>
            </table>
        </ReportShell>
    );
}

/* ---------------------------- Upcoming billing ---------------------------- */

function UpcomingTab() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = () => {
        setLoading(true);
        api.get("/reports/upcoming-billing", { params: { days } })
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    };
    useEffect(load, [days]);  // eslint-disable-line react-hooks/exhaustive-deps
    const csv = () => downloadCsv(`upcoming-billing-${days}d.csv`, data?.rows || [], [
        { label: "Account", value: "account_number" },
        { label: "Customer", value: "customer_name" },
        { label: "Email", value: "email" },
        { label: "Next bill", value: "next_billing_date" },
        { label: "Frequency", value: "billing_frequency" },
        { label: "Monthly rate", value: (r) => r.monthly_rate.toFixed(2) },
        { label: "Projected amount", value: (r) => r.projected_amount.toFixed(2) },
        { label: "Autopay", value: (r) => r.autopay ? "yes" : "no" },
    ]);
    return (
        <ReportShell
            title="Upcoming billing"
            subtitle={data
                ? `${data.count} customer(s) billing in next ${data.window_days}d · ${money(data.total_projected)} projected (${money(data.autopay_projected)} on autopay)`
                : "…"}
            onRefresh={load} onCsv={csv} loading={loading}
            extra={
                <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                    <SelectTrigger className="w-32" data-testid="upcoming-days-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {[7, 14, 30, 60, 90, 180].map((n) => (
                            <SelectItem key={n} value={String(n)}>Next {n} days</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            }>
            <table className="w-full text-sm" data-testid="upcoming-billing-table">
                <TableHead cols={["Account", "Customer", "Next bill", "Frequency", "Rate/mo", "Projected", "Autopay"]} />
                <tbody>
                    {(data?.rows || []).map((r) => (
                        <tr key={r.customer_id} className="border-t border-border hover:bg-secondary/40">
                            <td className="px-3 py-2 text-xs">{r.account_number}</td>
                            <td className="px-3 py-2">
                                <Link to={`/customers?highlight=${r.customer_id}`} className="hover:underline">{r.customer_name}</Link>
                                <div className="text-[11px] text-muted-foreground">{r.email}</div>
                            </td>
                            <td className="px-3 py-2 text-xs">{r.next_billing_date}</td>
                            <td className="px-3 py-2 text-xs">{titleCase(r.billing_frequency)}</td>
                            <td className="px-3 py-2 text-right text-xs text-muted-foreground">{money(r.monthly_rate)}</td>
                            <td className="px-3 py-2 text-right font-medium">{money(r.projected_amount)}</td>
                            <td className="px-3 py-2">
                                {r.autopay
                                    ? <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30 text-[10px]">AUTOPAY</Badge>
                                    : <span className="text-[10px] text-muted-foreground">manual</span>}
                            </td>
                        </tr>
                    ))}
                    {!loading && !data?.rows?.length && (
                        <tr><td colSpan={7} className="text-center py-10 text-sm text-muted-foreground">Nobody bills in this window.</td></tr>
                    )}
                </tbody>
            </table>
        </ReportShell>
    );
}

/* ------------------------------- Payments ------------------------------- */

function PaymentsTab() {
    const [start, setStart] = useState(isoTodayOffset(-30));
    const [end, setEnd] = useState(isoTodayOffset(0));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = () => {
        setLoading(true);
        api.get("/reports/payments", { params: { start, end } })
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    };
    useEffect(load, [start, end]);  // eslint-disable-line react-hooks/exhaustive-deps
    const csv = () => downloadCsv(`payments-${start}-to-${end}.csv`, data?.rows || [], [
        { label: "Date", value: "date" },
        { label: "Account", value: "account_number" },
        { label: "Customer", value: "customer_name" },
        { label: "Invoice", value: "invoice_id" },
        { label: "Method", value: "method" },
        { label: "Amount", value: (r) => r.amount.toFixed(2) },
    ]);
    return (
        <ReportShell
            title="Payment history"
            subtitle={data ? `${data.count} payment(s) · ${money(data.total)} received` : "…"}
            onRefresh={load} onCsv={csv} loading={loading}
            extra={
                <div className="flex items-end gap-2">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider">From</Label>
                        <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                               className="w-36" data-testid="payments-start-date" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider">To</Label>
                        <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                               className="w-36" data-testid="payments-end-date" />
                    </div>
                </div>
            }>
            {data?.by_day?.length > 0 && (
                <div style={{ width: "100%", height: 180 }} className="mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.by_day}>
                            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" fontSize={10} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                            <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11 }} />
                            <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
            <table className="w-full text-sm" data-testid="payments-table">
                <TableHead cols={["Date", "Account", "Customer", "Method", "Amount"]} />
                <tbody>
                    {(data?.rows || []).map((r) => (
                        <tr key={r.payment_id} className="border-t border-border hover:bg-secondary/40">
                            <td className="px-3 py-2 text-xs">{r.date}</td>
                            <td className="px-3 py-2 text-xs">{r.account_number || "—"}</td>
                            <td className="px-3 py-2">
                                <Link to={`/customers?highlight=${r.customer_id}`} className="hover:underline">
                                    {r.customer_name || <span className="text-muted-foreground">(unknown)</span>}
                                </Link>
                            </td>
                            <td className="px-3 py-2 text-[11px] uppercase text-muted-foreground">{r.method}</td>
                            <td className="px-3 py-2 text-right font-medium">{money(r.amount)}</td>
                        </tr>
                    ))}
                    {!loading && !data?.rows?.length && (
                        <tr><td colSpan={5} className="text-center py-10 text-sm text-muted-foreground">No payments in this window.</td></tr>
                    )}
                </tbody>
            </table>
        </ReportShell>
    );
}

/* ------------------------------- Revenue ------------------------------- */

function RevenueTab() {
    const [months, setMonths] = useState(12);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = () => {
        setLoading(true);
        api.get("/reports/revenue-summary", { params: { months } })
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    };
    useEffect(load, [months]);  // eslint-disable-line react-hooks/exhaustive-deps
    const csv = () => downloadCsv(`revenue-${months}mo.csv`, data?.rows || [], [
        { label: "Month", value: "month" },
        { label: "Revenue", value: (r) => r.revenue.toFixed(2) },
        { label: "Invoices", value: "invoices" },
    ]);
    return (
        <ReportShell
            title="Revenue summary"
            subtitle={data ? `${money(data.total_revenue)} over ${months} months · ${data.total_invoices} paid invoices` : "…"}
            onRefresh={load} onCsv={csv} loading={loading}
            extra={
                <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                    <SelectTrigger className="w-36" data-testid="revenue-months-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {[3, 6, 12, 24, 36].map((n) => (
                            <SelectItem key={n} value={String(n)}>Last {n} months</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            }>
            <div style={{ width: "100%", height: 260 }} className="mb-6">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data?.rows || []}>
                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" fontSize={10} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                        <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11 }} />
                        <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <table className="w-full text-sm" data-testid="revenue-table">
                <TableHead cols={["Month", "Revenue", "Invoices"]} />
                <tbody>
                    {(data?.rows || []).slice().reverse().map((r) => (
                        <tr key={r.month} className="border-t border-border hover:bg-secondary/40">
                            <td className="px-3 py-2 text-xs">{r.month}</td>
                            <td className="px-3 py-2 text-right font-medium">{money(r.revenue)}</td>
                            <td className="px-3 py-2 text-right text-xs text-muted-foreground">{r.invoices}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </ReportShell>
    );
}

/* --------------------------- Extra charges --------------------------- */

function ExtraChargesTab() {
    const [status, setStatus] = useState("pending");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = () => {
        setLoading(true);
        api.get("/reports/extra-charges", { params: { status } })
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    };
    useEffect(load, [status]);  // eslint-disable-line react-hooks/exhaustive-deps
    const csv = () => downloadCsv(`extra-charges-${status}.csv`, data?.rows || [], [
        { label: "Added", value: (r) => (r.created_at || "").slice(0, 10) },
        { label: "Account", value: "account_number" },
        { label: "Customer", value: "customer_name" },
        { label: "Description", value: "description" },
        { label: "Amount", value: (r) => Number(r.amount || 0).toFixed(2) },
        { label: "Added by", value: "created_by_name" },
        { label: "Role", value: "created_by_role" },
        { label: "Status", value: "status" },
        { label: "Applied to invoice", value: "applied_invoice_id" },
    ]);
    return (
        <ReportShell
            title="Extra charges on route"
            subtitle={data
                ? `${data.count} charge(s) · ${money(data.total_amount)} · showing ${status}`
                : "…"}
            onRefresh={load} onCsv={csv} loading={loading}
            extra={
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-44" data-testid="extras-status-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="pending">Pending (not yet billed)</SelectItem>
                        <SelectItem value="applied">Applied (already invoiced)</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                </Select>
            }>
            <table className="w-full text-sm" data-testid="extra-charges-table">
                <TableHead cols={["Added", "Customer", "Description", "Added by", "Status", "Amount"]} />
                <tbody>
                    {(data?.rows || []).map((r) => (
                        <tr key={r.id} className="border-t border-border hover:bg-secondary/40">
                            <td className="px-3 py-2 text-xs whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                            <td className="px-3 py-2">
                                <Link to={`/customers?highlight=${r.customer_id}`} className="hover:underline">
                                    {r.customer_name || <span className="text-muted-foreground">(unknown)</span>}
                                </Link>
                                {r.account_number && (
                                    <div className="text-[11px] text-muted-foreground">{r.account_number}</div>
                                )}
                            </td>
                            <td className="px-3 py-2">{r.description}</td>
                            <td className="px-3 py-2 text-xs">
                                {r.created_by_name}
                                <div className="text-[11px] text-muted-foreground">{titleCase(r.created_by_role || "")}</div>
                            </td>
                            <td className="px-3 py-2">
                                {r.status === "applied"
                                    ? <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30 text-[10px]">INVOICED</Badge>
                                    : <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/30 text-[10px]">PENDING</Badge>}
                            </td>
                            <td className="px-3 py-2 text-right font-medium">{money(r.amount)}</td>
                        </tr>
                    ))}
                    {!loading && !data?.rows?.length && (
                        <tr><td colSpan={6} className="text-center py-10 text-sm text-muted-foreground">
                            No {status === "all" ? "" : status + " "}extra charges to show.
                            {status === "pending" && " Drivers can log them from the stop card when they run into an extra bag, contamination, or bulk pickup."}
                        </td></tr>
                    )}
                </tbody>
            </table>
        </ReportShell>
    );
}

/* --------------------------- Route performance --------------------------- */

function RoutesTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const load = () => {
        setLoading(true);
        api.get("/reports/route-performance")
            .then(({ data }) => setData(data))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);
    const csv = () => downloadCsv("route-performance.csv", data?.rows || [], [
        { label: "Route", value: "route_name" },
        { label: "Day", value: "day" },
        { label: "Total stops", value: "total_stops" },
        { label: "Completed", value: "completed" },
        { label: "Cart not out", value: "cart_not_out" },
        { label: "Missed", value: "missed" },
        { label: "Pending", value: "pending" },
        { label: "With photo", value: "with_photo" },
        { label: "Completion %", value: (r) => `${r.completion_pct}%` },
    ]);
    return (
        <ReportShell
            title="Route performance"
            subtitle={data ? `${data.route_count} route(s) currently loaded` : "…"}
            onRefresh={load} onCsv={csv} loading={loading}>
            <table className="w-full text-sm" data-testid="route-performance-table">
                <TableHead cols={["Route", "Day", "Stops", "Done", "Missed", "Photos", "%"]} />
                <tbody>
                    {(data?.rows || []).map((r) => (
                        <tr key={r.route_id} className="border-t border-border hover:bg-secondary/40">
                            <td className="px-3 py-2">
                                <Link to={`/routing?route=${r.route_id}`} className="hover:underline">{r.route_name}</Link>
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{titleCase(r.day || "—")}</td>
                            <td className="px-3 py-2 text-right">{r.total_stops}</td>
                            <td className="px-3 py-2 text-right">
                                <span className="text-emerald-600 font-medium">{r.completed}</span>
                                {r.cart_not_out > 0 && <span className="text-amber-600 ml-1 text-xs">+{r.cart_not_out}cno</span>}
                            </td>
                            <td className="px-3 py-2 text-right">
                                {r.missed > 0
                                    ? <Badge variant="destructive" className="text-[10px]">{r.missed}</Badge>
                                    : <span className="text-xs text-muted-foreground">0</span>}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-muted-foreground">{r.with_photo}</td>
                            <td className="px-3 py-2 text-right font-medium">{r.completion_pct}%</td>
                        </tr>
                    ))}
                    {!loading && !data?.rows?.length && (
                        <tr><td colSpan={7} className="text-center py-10 text-sm text-muted-foreground">No routes loaded yet.</td></tr>
                    )}
                </tbody>
            </table>
        </ReportShell>
    );
}

/* ------------------------ Shared table shell ------------------------ */

function ReportShell({ title, subtitle, onRefresh, onCsv, loading, extra, children }) {
    return (
        <Card>
            <CardContent className="p-0">
                <div className="p-4 border-b border-border flex flex-col md:flex-row md:items-end justify-between gap-3">
                    <div className="min-w-0">
                        <h3 className="font-heading text-lg font-semibold">{title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {extra}
                        <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading}
                                data-testid="report-refresh">
                            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Refresh
                        </Button>
                        <Button size="sm" onClick={onCsv} data-testid="report-csv">
                            <Download className="w-3.5 h-3.5 mr-1.5" />CSV
                        </Button>
                    </div>
                </div>
                <div className="overflow-auto">
                    {children}
                </div>
            </CardContent>
        </Card>
    );
}

function TableHead({ cols }) {
    return (
        <thead className="bg-secondary/50 sticky top-0 z-10">
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {cols.map((c, i) => (
                    <th key={c} className={`px-3 py-2 ${i === cols.length - 1 ? "text-right" : "text-left"}`}>{c}</th>
                ))}
            </tr>
        </thead>
    );
}
